import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";

/**
 * Touch / stylus / mouse canvas with pen, eraser, clear, and undo.
 * Exposes getDataUrl(), clear(), isEmpty() via ref.
 */
const HandwritingCanvas = forwardRef(function HandwritingCanvas(
  { tool = "pen", className = "", onStrokeEnd },
  ref
) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const strokesRef = useRef([]);
  const currentStrokeRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr;
    const h = canvas.height / dpr;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);

    strokesRef.current.forEach((stroke) => {
      if (!stroke.points.length) return;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = stroke.width;
      if (stroke.tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = stroke.color;
      }
      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      for (let i = 1; i < stroke.points.length; i += 1) {
        ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
      }
      ctx.stroke();
    });
    ctx.globalCompositeOperation = "source-over";
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(Math.floor(rect.width), 1);
    const height = Math.max(Math.floor(rect.height), 1);

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    redraw();
  }, [redraw]);

  useEffect(() => {
    resizeCanvas();
    const observer = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("orientationchange", resizeCanvas);
    return () => {
      observer.disconnect();
      window.removeEventListener("orientationchange", resizeCanvas);
    };
  }, [resizeCanvas]);

  const getPoint = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const beginStroke = (event) => {
    if (event.pointerType === "touch" || event.pointerType === "pen") {
      event.preventDefault();
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(event.pointerId);

    const point = getPoint(event);
    drawingRef.current = true;
    lastPointRef.current = point;
    currentStrokeRef.current = {
      tool,
      color: "#111827",
      width: tool === "eraser" ? 18 : 2.5,
      points: [point],
    };
    strokesRef.current = [...strokesRef.current, currentStrokeRef.current];
  };

  const moveStroke = (event) => {
    if (!drawingRef.current || !currentStrokeRef.current) return;
    event.preventDefault();
    const point = getPoint(event);
    currentStrokeRef.current.points.push(point);
    lastPointRef.current = point;
    redraw();
  };

  const endStroke = (event) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    currentStrokeRef.current = null;
    setHasInk(strokesRef.current.some((s) => s.tool === "pen" && s.points.length > 0));
    if (onStrokeEnd) onStrokeEnd();
    try {
      canvasRef.current?.releasePointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !hasInk,
    clear: () => {
      strokesRef.current = [];
      setHasInk(false);
      redraw();
    },
    undo: () => {
      strokesRef.current = strokesRef.current.slice(0, -1);
      setHasInk(strokesRef.current.some((s) => s.tool === "pen" && s.points.length > 0));
      redraw();
    },
    getDataUrl: () => {
      const canvas = canvasRef.current;
      if (!canvas) return "";
      return canvas.toDataURL("image/png");
    },
  }));

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full min-h-[220px] rounded-md border border-gray-300 bg-white overflow-hidden ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="block w-full h-full touch-none cursor-crosshair"
        style={{ touchAction: "none" }}
        onPointerDown={beginStroke}
        onPointerMove={moveStroke}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
        onPointerLeave={(e) => {
          if (drawingRef.current) endStroke(e);
        }}
      />
    </div>
  );
});

HandwritingCanvas.propTypes = {
  tool: PropTypes.oneOf(["pen", "eraser"]),
  className: PropTypes.string,
  onStrokeEnd: PropTypes.func,
};

export default HandwritingCanvas;
